/**
 * OnlyOffice — cliente da sessão de edição (Story E14.2.3).
 *
 * Bate em `POST /onlyoffice/sessions/{id}` (E14.2.2). O `baseURL` do axios já
 * inclui `/api/v1` (convenção do `documentApi`/`equipmentApi`) — daí o path
 * relativo sem prefixo. O backend devolve 503 quando o OnlyOffice não está
 * configurado no ambiente (ex.: dev box sem o container), 403 sem permissão e
 * 404 quando o doc não existe / é de outro tenant.
 */
import { api } from "./api";
import type { EditorSessionResponse } from "@/lib/onlyoffice-iframe-wrapper";

/** Resposta de `POST /onlyoffice/documents/{id}/export-pdf` (E14.2.5). */
export interface ExportPdfResponse {
  /** ID do novo Document PDF criado no Drive (mesma pasta do original). */
  document_id: string;
  /** Nome do arquivo PDF gerado. */
  name: string;
  /** URL presignada pra baixar o PDF imediatamente. */
  download_url: string;
}

/**
 * `visualizar` abre só para ler também quem pode editar (o "Visualizar" e o duplo clique do
 * Drive). Quem decide de verdade é o servidor: ele emite o token de acesso com `read`.
 */
export type ModoDoEditor = "editar" | "visualizar";

export const onlyofficeApi = {
  openEditorSession: async (
    documentId: string,
    modo: ModoDoEditor = "editar"
  ): Promise<EditorSessionResponse> => {
    const response = await api.post("/onlyoffice/sessions/" + documentId, null, {
      params: { modo },
    });
    return response.data as EditorSessionResponse;
  },

  /**
   * Exporta um documento Office do Drive em PDF (FR-E14-11). O backend converte
   * via OnlyOffice, salva o PDF como novo Document na mesma pasta e devolve a
   * `download_url`. 503 = ambiente sem OnlyOffice; 415 = tipo não exportável;
   * 502 = falha na conversão.
   */
  exportPdf: async (documentId: string): Promise<ExportPdfResponse> => {
    const response = await api.post("/onlyoffice/documents/" + documentId + "/export-pdf");
    return response.data as ExportPdfResponse;
  },
};
